import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { cookieNames, useStepper } from '@monorepo/common';

import cookies from 'js-cookie';
import { useDispatch } from 'react-redux';
import { StepperInfoPanel } from './StepperInfoPanel';

import {
  makeStaticValuesForStep1,
  makeStaticValuesForStep2,
  makeStaticValuesForStep3,
  makeStaticValuesForStep4,
  makeStaticValuesForStep5
} from './staticValues';

import { IStep1Form } from './Step1/formInterface';
import { IStep2Form } from './Step2/formInterfaces';
import { IStep3Form } from './Step3/formInterface';
import { IStep4Form } from './Step4/formInterface';
import { IStep5Form } from './Step5/formInterfaces';

import { Step1 } from './Step1';
import { Step2 } from './Step2';
import { Step3 } from './Step3';
import { Step4 } from './Step4';
import { Step5 } from './Step5';
import { Step6 } from './Step6';
import { Step0 } from './Step0/Step0';
import { useGetTempDataQuery, useSaveTempDataMutation } from '../../../services/servicePostPrepaidListing';
import { showExtraDropButton } from '../../../features/sliceStepper';
import { useAppSelector } from '../../../store/hooks';
import { showNotification } from '../../../features/sliceNotification';
import { AllSteps } from './interfaceSteps';
import { LeavePageGuard, UNPOSTED_LISTING_MESSAGE } from '../../../components/LeavePageGuard';

interface IStepperPanelProps {
  isComplete?: boolean;
  // True as soon as the listing is posted, before the page moves on to the receipt.
  isPosted?: boolean;
  finalRequest: (value: AllSteps) => void;
}

export const StepperPanel = ({ finalRequest, isComplete, isPosted }:IStepperPanelProps) => {
  const dispatch = useDispatch();
  const [isGetTempData, setIsGetTempData] = useState(true);
  const [isRequest, setIsRequest] = useState<boolean>(false);
  const [stepperIsDropped, setStepperIsDropped] = useState<boolean>(false);

  const { isStepperDrop } = useAppSelector(({ stepperOptions }) => ({
    isStepperDrop: stepperOptions.isDrop
  }));

  const [saveTempData, { data: quidFromTempData, error: errSavedData }] = useSaveTempDataMutation();
  const [isFirstLoadPage, setIsFirstLoadPage] = useState(true);

  const { data: getSavedTempData, isLoading: isLoadingDataFromServer } = useGetTempDataQuery({ guid: cookies.get(cookieNames.tempPrepaidData) || '' }, {
    skip: isGetTempData
  });

  const { putSteps, onNext, onBack, onComplete, renderStepper, StepsButtons, dropStepper } = useStepper({
    orientation: 'vertical',
    isHideLeftInfoPanelAfterComplete: true,
    leftInfoPanel: (
      <StepperInfoPanel />
    ),
    finalComponent: null,
    isLoading: isLoadingDataFromServer
  });

  // Lazy initializers — every mount/reset gets its own object graph, so no two
  // step states ever share the `itemLocation` singleton.
  const [step1, setStep1] = useState<IStep1Form>(makeStaticValuesForStep1);
  const [step2, setStep2] = useState<IStep2Form>(makeStaticValuesForStep2);
  const [step3, setStep3] = useState<IStep3Form>(makeStaticValuesForStep3);
  const [step4, setStep4] = useState<IStep4Form>(makeStaticValuesForStep4);
  const [step5, setStep5] = useState<IStep5Form>(makeStaticValuesForStep5);

  const finalStepSubmit = useCallback(() => {
    finalRequest(
      {
        step1,
        step2,
        step3,
        step4,
        step5
      }
    );
  }, [step1, step2, step3, step4, step5]);

  const steps = useMemo(() => [
    {
      label: 'Listing Type',
      content: (
        <Step0
          setIsRequest={setIsRequest}
          Nav={StepsButtons}
          onNext={onNext}
        />
      )
    },
    {
      label: 'Basic Job Details',
      content: (
        <Step1
          defaultData={step1}
          setStep1={setStep1}
          Nav={StepsButtons}
          onBack={onBack}
          onNext={onNext}
        />
      )
    },
    {
      label: 'Compensation & Location',
      content: (
        <Step2
          defaultData={step2}
          setStep2={setStep2}
          Nav={StepsButtons}
          onNext={onNext}
          onBack={onBack}
        />
      )
    },
    {
      label: 'Job Description',
      content: (
        <Step3
          setStep3={setStep3}
          defaultData={step3}
          Nav={StepsButtons}
          onNext={onNext}
          onBack={onBack}
        />
      )
    },
    {
      label: 'Upload Logo',
      content: (
        <Step4
          setStep4={setStep4}
          defaultData={step4}
          Nav={StepsButtons}
          onNext={onNext}
          onBack={onBack}
        />
      )
    },
    {
      label: 'Contact and Application Details',
      content: (
        <Step5
          defaultValue={step5}
          setStep5={setStep5}
          Nav={StepsButtons}
          onNext={onNext}
          onBack={onBack}
        />
      )
    },
    {
      label: 'Terms and Conditions',
      content: (
        <Step6
          isEdited={false}
          Nav={StepsButtons}
          onNext={onNext}
          onBack={onBack}
          onComplete={onComplete}
          finalRequest={finalStepSubmit}
        />
      )
    },
  ], [step1, step2, step3, step4, step5]);

  useEffect(() => {
    dispatch(showExtraDropButton({ tempKay: cookieNames.tempPrepaidData }));
    putSteps(steps);
    if (!isFirstLoadPage && isRequest) {
      saveTempData({
        body: {
          step1,
          step2,
          step3,
          step4,
          step5,
        }
      });
    }
  }, [step1, step2, step3, step4, step5, isRequest]);

  useEffect(() => {
    if (isRequest) {
      setIsFirstLoadPage(false);
    }
  }, [isRequest]);

  useEffect(() => {
    if (isFirstLoadPage && !cookies.get(cookieNames.tempPrepaidData)) {
      setIsFirstLoadPage(false);
    }
    if (isFirstLoadPage && cookies.get(cookieNames.tempPrepaidData)) {
      setIsGetTempData(false);
    }
  }, []);

  useEffect(() => {
    if (quidFromTempData && quidFromTempData.guid && !cookies.get(cookieNames.tempPrepaidData)) {
      cookies.set(cookieNames.tempPrepaidData, quidFromTempData.guid);
      dispatch(showExtraDropButton({ tempKay: cookieNames.tempPrepaidData }));
    }
  }, [quidFromTempData, errSavedData]);

  useEffect(() => {
    if (isStepperDrop === false && cookies.get(cookieNames.tempPrepaidData)) {
      if (isFirstLoadPage && getSavedTempData) {
        setStep1(getSavedTempData.step1);
        setStep2(getSavedTempData.step2);
        setStep3(getSavedTempData.step3);
        setStep4(getSavedTempData.step4);
        setStep5(getSavedTempData.step5);
      }
    }
  }, [getSavedTempData, isStepperDrop]);

  useEffect(() => {
    if (isComplete && !stepperIsDropped) {
      setStepperIsDropped(true);
      onComplete();
      // On a successful post the page redirects to the receipt, unmounting this
      // component while the timer is still pending. Cancel it on cleanup so the
      // delayed setStepperIsDropped never runs on an unmounted component (React warns).
      const timeout = setTimeout(() => {
        setStepperIsDropped(false);
      }, 3000);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [isComplete]);

  // "Clear this job listing and start over". Every reset MUST go through a factory:
  // re-assigning the shared module constant is an Object.is no-op, so React bails,
  // no dependency of the publish effect above changes, and `putSteps` never re-fires
  // after `dropStepper()` emptied the steps — leaving the stepper blank for good.
  useEffect(() => {
    dispatch(showExtraDropButton({ tempKay: cookieNames.tempPrepaidData }));
    if (isStepperDrop) {
      setIsRequest(false);
      setStep1(makeStaticValuesForStep1());
      setStep2(makeStaticValuesForStep2());
      setStep3(makeStaticValuesForStep3());
      setStep4(makeStaticValuesForStep4());
      setStep5(makeStaticValuesForStep5());
      dispatch(showNotification({
        show: true,
        type: 'info',
        text: 'Form cleared'
      }));
      dropStepper();
    }
  }, [isStepperDrop]);

  return (
    <>
      {/* Armed from the first Next (isRequest; "Start over" clears it). It has to be off before
          the page pushes to the receipt, or that push would ask the user to confirm. */}
      <LeavePageGuard
        when={isRequest && !isPosted}
        message={UNPOSTED_LISTING_MESSAGE}
      />
      {renderStepper}
    </>
  );
};
